// Client-side KaCyber MoMo top-up initiation.
// Calls the `kacyber-proxy` edge function which holds KaCyber API credentials
// server-side and proxies the request to KaCyber's collections API.

import { supabase } from "@/integrations/supabase/client";

const PROXY_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/kacyber-proxy`;

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY;
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  };
}

/** Normalise a Ugandan phone number to 256XXXXXXXXX format (no +). */
export function normaliseMsisdn(input: string): string {
  let s = input.replace(/\s/g, "").replace(/^\+/, "");
  if (s.startsWith("256")) return s;
  if (s.startsWith("0")) return "256" + s.slice(1);
  if (s.startsWith("7") && s.length === 9) return "256" + s;
  return s;
}

/** Look up the account holder name for a given MSISDN + network. */
export async function lookupAccount(data: {
  msisdn: string;
  network: "MTN" | "AIRTEL";
}): Promise<{ accountName?: string; success: boolean; message?: string }> {
  try {
    const res = await fetch(PROXY_URL, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify({ action: "lookup", ...data }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return { success: false, message: body.error ?? `Lookup failed (${res.status})` };
    }
    const body = await res.json();
    return {
      success: true,
      accountName: body.accountName ?? body.customerName ?? body.name,
      message: body.message,
    };
  } catch (e: unknown) {
    return { success: false, message: e instanceof Error ? e.message : "Network error" };
  }
}

/** Initiate a KaCyber MoMo collection (push to the customer's phone). */
export async function initiateMomoTopup(data: {
  wallet_id: string;
  amount_ugx: number;
  channel: "mtn_momo" | "airtel_money";
  phone: string;
  customer_name?: string;
  narration?: string;
  external_reference?: string;
  topup_id?: string;
  fee_ugx?: number;
}): Promise<{ reference: string; topup_id: string; kacyber_reference?: string; prn?: string; sms_sent?: boolean; sms_message?: string; kacyber_response?: Record<string, unknown> }> {
  const external_reference =
    data.external_reference ??
    `SCH-${crypto.randomUUID().slice(0, 8).toUpperCase()}`;

  const network = data.channel === "mtn_momo" ? "MTN" : "AIRTEL";
  const msisdn = normaliseMsisdn(data.phone);

  const res = await fetch(PROXY_URL, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify({
      action: "collect",
      wallet_id:          data.wallet_id,
      topup_id:           data.topup_id,
      amount_ugx:         data.amount_ugx,
      fee_ugx:            data.fee_ugx ?? 0,
      msisdn,
      network,
      customer_name:      data.customer_name,
      narration:           data.narration,
      external_reference,
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const dbg = body.debug ? ` [env:${body.debug.env} key:${body.debug.apiKey} url:${body.debug.baseUrl}]` : "";
    throw new Error((body.error ?? `KaCyber request failed (${res.status})`) + dbg);
  }

  const body = await res.json();

  // Return the external_reference as `reference` so the existing polling
  // loop (which polls `topups` by topup_id) continues to work.  The
  // kacyber_reference is also surfaced for display.
  return {
    reference:         external_reference,
    topup_id:          data.topup_id ?? external_reference,
    kacyber_reference: body.kacyber_reference,
    prn:               body.prn,
    sms_sent:          body.sms?.success ?? false,
    sms_message:       body.sms?.message,
    kacyber_response:  body.kacyber_response,
  };
}

/** Check the live status of a KaCyber transaction by reference. */
export async function checkKacyberStatus(reference: string): Promise<{
  status?: string;
  success: boolean;
  message?: string;
}> {
  try {
    const res = await fetch(PROXY_URL, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify({ action: "status", reference }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return { success: false, message: body.error ?? `Status check failed (${res.status})` };
    }
    const body = await res.json();
    return { success: true, status: body.status, message: body.message };
  } catch (e: unknown) {
    return { success: false, message: e instanceof Error ? e.message : "Network error" };
  }
}

/** Initiate a customer-initiated USSD payment (no push — parent dials USSD themselves). */
export async function initiateUssdPayment(data: {
  wallet_id: string;
  topup_id?: string;
  amount_ugx: number;
  fee_ugx?: number;
  channel: "mtn_momo" | "airtel_money";
  phone: string;
  customer_name?: string;
  external_reference: string;
}): Promise<{
  prn: string;
  merchant_msisdn: string;
  merchant_name: string;
  ussd_code: string;
  send_money_steps: string[];
  sms: { success: boolean; message: string };
  external_reference: string;
}> {
  const network = data.channel === "mtn_momo" ? "MTN" : "AIRTEL";
  const msisdn = normaliseMsisdn(data.phone);

  const res = await fetch(PROXY_URL, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify({
      action: "ussd-initiate",
      wallet_id: data.wallet_id,
      topup_id: data.topup_id,
      amount_ugx: data.amount_ugx,
      fee_ugx: data.fee_ugx ?? 0,
      msisdn,
      network,
      customer_name: data.customer_name,
      external_reference: data.external_reference,
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `USSD initiation failed (${res.status})`);
  }

  return await res.json();
}

/** Poll for USSD payment confirmation from our DB (admin verification or callback). */
export async function pollUssdPaymentStatus(externalReference: string): Promise<{
  payment_status: "completed" | "failed" | "pending";
  verification_status?: string;
  reason?: string;
}> {
  try {
    const res = await fetch(PROXY_URL, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify({ action: "ussd-poll", external_reference: externalReference }),
    });
    if (!res.ok) return { payment_status: "pending" };
    const body = await res.json();
    return {
      payment_status: body.payment_status ?? "pending",
      verification_status: body.verification_status,
      reason: body.reason,
    };
  } catch {
    return { payment_status: "pending" };
  }
}

/** Fetch pending USSD payments for admin verification (school admin / superadmin). */
export async function listUssdVerifications(): Promise<{
  verifications: Array<{
    id: string;
    external_reference: string;
    msisdn: string;
    network: string;
    amount_ugx: number;
    prn: string;
    status: string;
    admin_note: string | null;
    created_at: string;
  }>;
}> {
  const res = await fetch(PROXY_URL, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify({ action: "list-ussd-verifications" }),
  });
  if (!res.ok) return { verifications: [] };
  const body = await res.json();
  return { verifications: body.verifications ?? [] };
}

/** Admin approves or rejects a USSD payment. */
export async function verifyUssdPayment(data: {
  external_reference: string;
  approved: boolean;
  admin_note?: string;
}): Promise<{ status: string }> {
  const res = await fetch(PROXY_URL, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify({ action: "admin-verify-ussd", ...data }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? "Verification failed");
  }
  return await res.json();
}

/**
 * Poll the KaCyber status API for a specific transaction.
 * The proxy checks KaCyber directly and credits the wallet server-side if completed.
 * Returns "completed", "failed", or "pending".
 */
export async function pollKacyberPaymentStatus(externalReference: string, kacyberReference: string): Promise<{
  payment_status: "completed" | "failed" | "pending" | "expired";
  kc_status?: string;
  kc_error?: string;
}> {
  try {
    const res = await fetch(PROXY_URL, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify({
        action: "poll-status",
        external_reference: externalReference,
        kacyber_reference: kacyberReference,
      }),
    });
    if (!res.ok) return { payment_status: "pending" };
    const body = await res.json();
    return {
      payment_status: body.payment_status ?? "pending",
      kc_status: body.kc_status,
      kc_error: body.kc_error,
    };
  } catch {
    return { payment_status: "pending" };
  }
}

/** Simulate a completed payment (demo/testing fallback).
 * Credits the wallet as if KaCyber confirmed the push. */
export async function simulatePayment(externalReference: string): Promise<{
  payment_status: "completed" | "failed" | "pending" | "expired";
}> {
  try {
    const res = await fetch(PROXY_URL, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify({ action: "simulate-payment", external_reference: externalReference }),
    });
    if (!res.ok) return { payment_status: "pending" };
    const body = await res.json();
    return { payment_status: body.payment_status ?? "pending" };
  } catch {
    return { payment_status: "pending" };
  }
}
